import { FC, useMemo, useState } from 'react';
import ModalWrapper from '../../components/modals/ModalWrapper';
import ModalHeader from '../../components/modals/ModalHeader';
import {
  IonContent,
  IonIcon,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonSpinner,
} from '@ionic/react';
import { checkmark, trash } from 'ionicons/icons';
import { StoreInterface } from '../../../../store/types';
import { useSelector } from 'react-redux';
import { PaymentMethod, PaymentMethodBrand } from '../../../../services/exports/Constants';
import { Modals } from '../../exports/Enums';
import MasterCardIcon from '../../../../assets/icons/payment-methods/MasterCardIcon';
import VisaIcon from '../../../../assets/icons/payment-methods/VisaIcon';
import AmericanExpressIcon from '../../../../assets/icons/payment-methods/AmericanExpressIcon';
import { collect } from 'collect.js';
import { PaymentOptionPropertiesInterface } from '../../../../services/exports/Interfaces';
import PaymentManager from '../../../../services/api/PaymentManager';
import { IModal } from '../../router/ModalRouter';
import { useTranslation } from 'react-i18next';
import GooglePayIcon from '../../../../assets/icons/payment-methods/GooglePayIcon';
import ApplePayIcon from '../../../../assets/icons/payment-methods/ApplePayIcon';
import PayPalIcon from '../../../../assets/icons/payment-methods/PayPalIcon';

interface IPaymentOptionIcon {
  icon: React.ReactNode;
  label: string;
  brand?: PaymentMethodBrand;
}

interface IPaymentOption {
  id: PaymentMethod;
  label: string;
  icons: IPaymentOptionIcon[];
  paymentOption: PaymentOptionPropertiesInterface;
  onClick?: () => void;
}

const PaymentMethodsModal = ({ openModal, goBack, closeModal }: Partial<IModal>): JSX.Element => {
  const { t } = useTranslation(null, {
    keyPrefix:
      'Modules:Mobile:Features:Profile:Components:ProfileModal:NestedModals:PaymentMethods:PaymentMethodsModal',
  });
  const { payment_options } = useSelector((state: StoreInterface) => state.paymentOptions);
  const [deletingPaymentOption, setDeletingPaymentOption] = useState<boolean>(false);

  const removePaymentOption = async (id: number): Promise<void> => {
    setDeletingPaymentOption(true);
    await PaymentManager.removePaymentOption(id);
    setDeletingPaymentOption(false);
  };

  const iconClassName = 'h-[14px]';

  const paymentOptions = useMemo(
    () =>
      collect([
        {
          id: PaymentMethod.Card,
          label: t(`payment_options.${PaymentMethod.Card}`),
          icons: [
            {
              icon: <MasterCardIcon className={iconClassName} />,
              label: 'MasterCard',
              brand: PaymentMethodBrand.MasterCard,
            },
            {
              icon: <AmericanExpressIcon className={iconClassName} />,
              label: 'American Express',
              brand: PaymentMethodBrand.AmericanExpress,
            },
            {
              icon: <VisaIcon className={iconClassName} />,
              label: 'Visa',
              brand: PaymentMethodBrand.Visa,
            },
          ],
          onClick: () => openModal({ id: Modals.AddCreditCard }),
        },
        {
          id: PaymentMethod.GooglePay,
          label: 'Google Pay',
          icons: [
            {
              icon: <GooglePayIcon className={iconClassName} />,
            },
          ],
        },
        {
          id: PaymentMethod.ApplePay,
          label: 'Apple Pay',
          icons: [
            {
              icon: <ApplePayIcon className={iconClassName} />,
            },
          ],
        },
        {
          id: PaymentMethod.Paypal,
          label: 'Paypal',
          icons: [
            {
              icon: <PayPalIcon className={iconClassName} />,
            },
          ],
        },
      ])
        .map((item) => {
          const paymentOption = collect(payment_options).firstWhere('payment_method_id', item.id);
          return {
            ...item,
            paymentOption,
            label:
              paymentOption &&
              paymentOption.payment_method_details &&
              paymentOption.payment_method_id === PaymentMethod.Card
                ? `**** **** **** ${paymentOption.payment_method_details}`
                : item.label,
            icons: paymentOption
              ? collect<IPaymentOptionIcon>(item.icons)
                  .filter((item) => item?.brand === paymentOption?.payment_method_brand)
                  .toArray()
              : item.icons,
          };
        })
        .toArray() as IPaymentOption[],
    [payment_options],
  );

  const renderPaymentOption: FC<IPaymentOption> = ({ icons, label, paymentOption, onClick }): JSX.Element => {
    const hasPaymentOption = !!paymentOption;
    return (
      <IonItemSliding key={paymentOption?.id}>
        <IonItem
          button={!hasPaymentOption && !!onClick}
          onClick={!hasPaymentOption ? onClick : undefined}
          className="flex justify-between"
        >
          <IonLabel>
            <h6 className="!font-medium">{label}</h6>
          </IonLabel>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              {icons.map((item) => (
                <div key={item.label}>{item.icon}</div>
              ))}
            </div>
            {paymentOption?.is_default && <IonIcon slot="end" icon={checkmark} />}
          </div>
        </IonItem>
        <IonItemOptions>
          {hasPaymentOption && (
            <IonItemOption color="danger" onClick={() => removePaymentOption(paymentOption.id)}>
              {deletingPaymentOption ? <IonSpinner name="crescent" /> : <IonIcon icon={trash} />}
            </IonItemOption>
          )}
        </IonItemOptions>
      </IonItemSliding>
    );
  };

  return (
    <ModalWrapper className="mt-4">
      <ModalHeader title={t('title')} onBack={goBack} onClose={closeModal} />
      <IonContent className="min-h-[75dvh] [&::part(scroll)]:py-4 space-y-md">
        <IonList>{paymentOptions.map(renderPaymentOption)}</IonList>
      </IonContent>
    </ModalWrapper>
  );
};

export default PaymentMethodsModal;
